import { useState } from 'react';
import toast from 'react-hot-toast';
import api from '../utils/api';

interface QuoteRequestButtonProps {
  itemId: number;
  itemType: 'boat' | 'motor';
  brand?: string;
  model?: string;
}

export function QuoteRequestButton({ itemId, itemType, brand = '', model = '' }: QuoteRequestButtonProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const response = await fetch(`${window.BOAT_TRADER_API?.rest_url || ''}boat-trader-v3/v1/quote-request`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          item_id: itemId,
          item_type: itemType,
          ...formData
        })
      });

      const data = await response.json();

      if (response.ok && data.success) {
        toast.success('Richiesta preventivo inviata!');
        setIsOpen(false);
        setFormData({ name: '', email: '', phone: '', message: '' });
      } else {
        toast.error(data.message || 'Errore nell\'invio della richiesta');
      }
    } catch (error) {
      toast.error('Errore nella connessione');
    } finally {
      setIsLoading(false);
    }
  };

  if (isOpen) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ backgroundColor: 'rgba(2,6,15,0.65)' }}>
        <div className="rounded-xl max-w-md w-full p-6 shadow-xl" style={{ backgroundColor: 'var(--au-surface-2, #fff)' }}>
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-semibold" style={{ color: 'var(--au-text, #111827)' }}>
              Richiedi Preventivo
            </h3>
            <button
              onClick={() => setIsOpen(false)}
              className="hover:opacity-70"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <p className="text-sm mb-4" style={{ color: 'var(--au-text-2, #6b7280)' }}>
            {brand} {model}
          </p>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-1" style={{ color: 'var(--au-text, #374151)' }}>
                Nome *
              </label>
              <input
                type="text"
                required
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                style={{ borderColor: 'var(--au-border, #d1d5db)', backgroundColor: 'var(--au-surface, #fff)', color: 'var(--au-text, #111827)' }}
                placeholder="Il tuo nome"
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-1" style={{ color: 'var(--au-text, #374151)' }}>
                Email *
              </label>
              <input
                type="email"
                required
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                style={{ borderColor: 'var(--au-border, #d1d5db)', backgroundColor: 'var(--au-surface, #fff)', color: 'var(--au-text, #111827)' }}
                placeholder="tua@email.it"
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-1" style={{ color: 'var(--au-text, #374151)' }}>
                Telefono
              </label>
              <input
                type="tel"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                style={{ borderColor: 'var(--au-border, #d1d5db)', backgroundColor: 'var(--au-surface, #fff)', color: 'var(--au-text, #111827)' }}
                placeholder="+39..."
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-1" style={{ color: 'var(--au-text, #374151)' }}>
                Messaggio
              </label>
              <textarea
                value={formData.message}
                onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                style={{ borderColor: 'var(--au-border, #d1d5db)', backgroundColor: 'var(--au-surface, #fff)', color: 'var(--au-text, #111827)' }}
                rows={3}
                placeholder="Descrivi cosa vorresti sapere..."
              />
            </div>

            <button
              type="submit"
              disabled={isLoading}
              className="w-full bg-primary-600 text-white py-2 px-4 rounded-lg font-medium hover:bg-primary-700 disabled:opacity-50 transition-colors"
            >
              {isLoading ? 'Invio in corso...' : 'Invia Richiesta'}
            </button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <button
      onClick={() => setIsOpen(true)}
      className="btn-quote-request flex items-center justify-center gap-2 w-full bg-primary-600 text-white py-3 px-4 rounded-lg font-medium hover:bg-primary-700 transition-colors"
    >
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
      </svg>
      Richiedi Preventivo
    </button>
  );
}
